import { useState, useEffect } from "react";
import { Calendar, Zap, Clock, ChevronUp, TrendingUp, DollarSign, Eye } from "lucide-react";
import { FMP_BASE, FMP_KEY, EARNINGS_SYS, HAIKU } from '../constants';
import { callClaude } from '../utils/callClaude';
import PageHeader from '../components/PageHeader';
import TickerBadge from '../components/TickerBadge';

export default function EarningsPage() {
  const [earnings,     setEarnings]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [briefs,       setBriefs]       = useState({});
  const [briefLoading, setBriefLoading] = useState({});
  const [expanded,     setExpanded]     = useState({});

  useEffect(() => {
    try { setBriefs(JSON.parse(localStorage.getItem("hma_earn_briefs") || "{}")); } catch(e) {}
    const from = new Date().toISOString().slice(0,10);
    const to   = new Date(Date.now() + 7*86400000).toISOString().slice(0,10);
    Promise.all([
      fetch(`${FMP_BASE}/stable/earnings-calendar?from=${from}&to=${to}&apikey=${FMP_KEY}`).then(r=>r.json()).catch(()=>[]),
      fetch(`${FMP_BASE}/stable/company-screener?marketCapMoreThan=2000000000&limit=1000&country=US&isActivelyTrading=true&apikey=${FMP_KEY}`).then(r=>r.json()).catch(()=>[]),
    ]).then(([earns, screen]) => {
      const screenMap = {};
      (Array.isArray(screen)?screen:[]).forEach(s=>{ screenMap[s.symbol]=s; });
      const lcSet = new Set(Object.keys(screenMap));
      const arr = (Array.isArray(earns)?earns:[])
        .filter(e=>e.symbol&&!e.symbol.includes(".")&&!e.symbol.includes("-")&&(lcSet.size===0||lcSet.has(e.symbol)))
        .map(e=>({ ...e, companyName: screenMap[e.symbol]?.companyName||e.symbol, sector: screenMap[e.symbol]?.sector||"", marketCap: screenMap[e.symbol]?.marketCap||0 }));
      setEarnings(arr);
      setLoading(false);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getPrep = async e => {
    const brief = briefs[e.symbol]?.data;
    if (brief) { setExpanded(p=>({...p,[e.symbol]:!p[e.symbol]})); return; }
    setBriefLoading(p=>({...p,[e.symbol]:true}));
    const msg = `${e.symbol} (${e.name||e.symbol}) reports on ${e.date}. EPS est: ${e.epsEstimated!=null?"$"+Number(e.epsEstimated).toFixed(2):"N/A"}. Rev est: ${e.revenueEstimated?"$"+(e.revenueEstimated/1e9).toFixed(2)+"B":"N/A"}.`;
    try {
      const result = await callClaude(EARNINGS_SYS, msg, 400, false, false, HAIKU);
      if (result?.watch) {
        const nb = {...briefs,[e.symbol]:{data:result,ts:Date.now()}};
        setBriefs(nb); setExpanded(p=>({...p,[e.symbol]:true}));
        try { localStorage.setItem("hma_earn_briefs",JSON.stringify(nb)); } catch(ex) {}
      }
    } catch(ex) {}
    setBriefLoading(p=>({...p,[e.symbol]:false}));
  };

  const today    = new Date().toISOString().slice(0,10);
  const tomorrow = new Date(Date.now()+86400000).toISOString().slice(0,10);
  const grouped  = {};
  earnings.forEach(e=>{ const d=e.date||"TBD"; if(!grouped[d])grouped[d]=[]; grouped[d].push(e); });
  const dates = Object.keys(grouped).sort();

  const fmtDate = d => d===today ? "Today" : d===tomorrow ? "Tomorrow" : new Date(d+"T12:00:00").toLocaleDateString("en-US",{weekday:"long",month:"short",day:"numeric"});
  const fmtRev  = v => v ? "$"+(v/1e9).toFixed(1)+"B" : null;
  const fmtEps  = v => v!=null ? "$"+Number(v).toFixed(2) : null;
  const avatarBg = sym => ["#1B4F8A","#a07030","#006630","#7B2FBE","#C41E3A","#0891B2"][sym.charCodeAt(0)%6];
  const sectorTag = s => {
    const sec = (s||"").toLowerCase();
    if (sec.includes("tech") || sec.includes("software") || sec.includes("semiconductor")) return { label:"TECH", color:"#60A5FA", bg:"rgba(96,165,250,0.1)" };
    if (sec.includes("financial") || sec.includes("bank") || sec.includes("insurance")) return { label:"FIN", color:"#e8c875", bg:"rgba(201,168,76,0.1)" };
    if (sec.includes("health") || sec.includes("pharma") || sec.includes("biotech") || sec.includes("medical")) return { label:"HLTH", color:"#34D399", bg:"rgba(52,211,153,0.1)" };
    if (sec.includes("energy") || sec.includes("oil") || sec.includes("gas") || sec.includes("utility")) return { label:"ENRG", color:"#FBBF24", bg:"rgba(251,191,36,0.1)" };
    if (sec.includes("consumer") && sec.includes("disc")) return { label:"CONS", color:"#F472B6", bg:"rgba(244,114,182,0.1)" };
    if (sec.includes("consumer") && sec.includes("stap")) return { label:"STPL", color:"#A78BFA", bg:"rgba(167,139,250,0.1)" };
    if (sec.includes("communication")) return { label:"COMM", color:"#38BDF8", bg:"rgba(56,189,248,0.1)" };
    if (sec.includes("industrial")) return { label:"INDS", color:"#94A3B8", bg:"rgba(148,163,184,0.1)" };
    if (sec.includes("material")) return { label:"MATL", color:"#6EE7B7", bg:"rgba(110,231,183,0.1)" };
    if (sec.includes("real estate")) return { label:"REIT", color:"#FCA5A5", bg:"rgba(252,165,165,0.1)" };
    if (sec.includes("auto") || sec.includes("vehicle")) return { label:"AUTO", color:"#C4B5FD", bg:"rgba(196,181,253,0.1)" };
    return { label:"EQT", color:"rgba(255,255,255,0.5)", bg:"rgba(255,255,255,0.06)" };
  };
  const fmtMktCap = v => v>=1e12 ? "$"+(v/1e12).toFixed(1)+"T" : v>=1e9 ? "$"+(v/1e9).toFixed(0)+"B" : null;

  return (
    <div style={{fontFamily:"'Inter',sans-serif",background:"#000",minHeight:"100vh",color:"#fff"}}>
      <style>{`html,body{background:#000!important}*{box-sizing:border-box}a{text-decoration:none}
        .hma-ecard{transition:transform .15s,box-shadow .15s;cursor:pointer}
        .hma-ecard:hover{transform:translateY(-2px);box-shadow:0 8px 32px rgba(0,0,0,0.6)}
        @media(max-width:768px){
          .hma-earn-grid{grid-template-columns:1fr!important}
          .hma-earn-pad{padding:24px 16px 80px!important}
          .hma-bottom-nav{display:flex!important}
          .hma-earn-title{font-size:26px!important}
          .hma-ecard-inner{padding:13px 14px 11px!important}
          .hma-earn-metrics{display:flex;flex-direction:row;gap:6px}
          .hma-earn-metric-box{padding:7px 9px!important}
          .hma-earn-metric-val{font-size:15px!important}
          .hma-earn-strip{margin-left:-14px!important;width:calc(100% + 28px)!important}
          .hma-earn-name{font-size:14px!important;min-height:unset!important}
          .hma-earn-bull-bear{grid-template-columns:1fr!important}
        }
      `}</style>
      <PageHeader active="earnings"/>
      <div className="hma-track-pad hma-earn-pad" style={{maxWidth:1100,margin:"0 auto",padding:"48px 40px 80px"}}>
        <div style={{marginBottom:36}}>
          <div style={{fontFamily:"'Cinzel',serif",fontSize:34,fontWeight:900,background:"linear-gradient(135deg,#ffe87a,#a07030)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",backgroundClip:"text",marginBottom:8}}>Earnings Calendar</div>
          <div style={{fontSize:14,color:"rgba(255,255,255,0.78)",maxWidth:560,lineHeight:1.7}}>Large-cap earnings this week ($2B+ market cap). Hit <span style={{color:"#e8c875",fontWeight:700,display:"inline-flex",alignItems:"center",gap:3,verticalAlign:"middle"}}><Zap size={13} strokeWidth={2} color="#e8c875"/>Pre-Game</span> for an instant bull/bear breakdown before the call.</div>
        </div>

        {loading ? (
          <div style={{textAlign:"center",padding:80,color:"rgba(255,255,255,0.3)",fontSize:14}}>
            <div style={{marginBottom:12,display:"flex",justifyContent:"center"}}><Calendar size={36} strokeWidth={1.5} color="rgba(201,168,76,0.4)"/></div>Loading earnings…
          </div>
        ) : dates.length===0 ? (
          <div style={{textAlign:"center",padding:80,color:"rgba(255,255,255,0.3)",fontSize:14}}>
            <div style={{marginBottom:12,display:"flex",justifyContent:"center"}}><Calendar size={36} strokeWidth={1.5} color="rgba(201,168,76,0.3)"/></div>No large-cap earnings this week. Check back soon.
          </div>
        ) : dates.map(date=>{
          const isToday = date===today;
          const isTomorrow = date===tomorrow;
          const sorted = [...grouped[date]].sort((a,b)=>(b.revenueEstimated||0)-(a.revenueEstimated||0));
          return (
            <div key={date} style={{marginBottom:44}}>
              {/* Date header */}
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:18}}>
                <div style={{
                  fontFamily:"'Cinzel',serif",fontSize:isToday?22:18,fontWeight:900,
                  color:isToday?"#e8c875":isTomorrow?"#fff":"rgba(255,255,255,0.5)",
                  letterSpacing:isToday?".02em":0
                }}>{fmtDate(date)}</div>
                {isToday && <span style={{fontSize:10,fontWeight:900,color:"#000",background:"#e8c875",borderRadius:20,padding:"3px 10px",letterSpacing:".1em"}}>TODAY</span>}
                {isTomorrow && <span style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.5)",background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:20,padding:"3px 10px"}}>TOMORROW</span>}
                <div style={{flex:1,height:1,background:isToday?"rgba(201,168,76,0.15)":"rgba(255,255,255,0.05)"}}/>
                <span style={{fontSize:11,color:"rgba(255,255,255,0.2)",fontWeight:600}}>{sorted.length} report{sorted.length!==1?"s":""}</span>
              </div>

              {/* Cards grid */}
              <div className="hma-earn-grid" style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14}}>
                {sorted.map(e=>{
                  const brief=briefs[e.symbol]?.data; const bl=briefLoading[e.symbol]; const open=expanded[e.symbol];
                  const stag = sectorTag(e.sector);
                  const mc = fmtMktCap(e.marketCap);
                  const accentClr = avatarBg(e.symbol);
                  return (
                    <div key={e.symbol} className="hma-ecard" style={{
                      background:`linear-gradient(160deg,#111 0%,#0A0A0A 100%)`,
                      border:`1px solid ${open?"rgba(201,168,76,0.35)":isToday?"rgba(201,168,76,0.12)":"rgba(255,255,255,0.07)"}`,
                      borderRadius:18,overflow:"hidden",
                      boxShadow:isToday?`0 0 0 1px rgba(201,168,76,0.06), 0 4px 24px rgba(0,0,0,0.4)`:"0 2px 12px rgba(0,0,0,0.3)"
                    }}>
                      {/* Colored top accent bar */}
                      <div style={{height:3,background:`linear-gradient(90deg,${accentClr},transparent)`}}/>

                      <div className="hma-ecard-inner" style={{padding:"16px 18px 14px"}}>
                        {/* Top row: logo + ticker + market cap */}
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                          <div style={{display:"flex",alignItems:"center",gap:10}}>
                            <TickerBadge symbol={e.symbol} size={38}/>
                            <div>
                              <a href={`/?t=${e.symbol}`} style={{fontFamily:"'Cinzel',serif",fontSize:18,fontWeight:900,color:"#e8c875",letterSpacing:".04em",display:"block",lineHeight:1}}>{e.symbol}</a>
                              <span style={{fontSize:11,fontWeight:800,letterSpacing:".08em",color:stag.color,background:stag.bg,border:`1px solid ${stag.color}30`,borderRadius:4,padding:"1px 5px",marginTop:3,display:"inline-block"}}>{stag.label}</span>
                            </div>
                          </div>
                          {mc && <span style={{fontSize:10,color:"rgba(255,255,255,0.35)",fontWeight:600,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:20,padding:"2px 8px"}}>{mc}</span>}
                        </div>

                        {/* Company full name */}
                        <div className="hma-earn-name" style={{fontSize:15,fontWeight:700,color:"#fff",lineHeight:1.35,marginBottom:4,minHeight:40,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>
                          {e.companyName}
                        </div>
                        {e.sector && <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",marginBottom:14,fontWeight:500}}>{e.sector}</div>}

                        {/* Metrics */}
                        <div className="hma-earn-metrics" style={{display:"flex",gap:8,marginBottom:14}}>
                          {fmtRev(e.revenueEstimated) ? (
                            <div className="hma-earn-metric-box" style={{flex:1,background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:10,padding:"9px 11px"}}>
                              <div style={{display:"flex",alignItems:"center",gap:4,fontSize:11,color:"rgba(255,255,255,0.28)",fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",marginBottom:4}}><TrendingUp size={8} strokeWidth={2}/> Rev Est</div>
                              <div className="hma-earn-metric-val" style={{fontSize:18,fontWeight:900,color:"#fff"}}>{fmtRev(e.revenueEstimated)}</div>
                            </div>
                          ) : null}
                          {fmtEps(e.epsEstimated) ? (
                            <div className="hma-earn-metric-box" style={{flex:1,background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:10,padding:"9px 11px"}}>
                              <div style={{display:"flex",alignItems:"center",gap:4,fontSize:11,color:"rgba(255,255,255,0.28)",fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",marginBottom:4}}><DollarSign size={8} strokeWidth={2}/> EPS Est</div>
                              <div className="hma-earn-metric-val" style={{fontSize:18,fontWeight:900,color:"#fff"}}>{fmtEps(e.epsEstimated)}</div>
                            </div>
                          ) : null}
                          {!fmtRev(e.revenueEstimated) && !fmtEps(e.epsEstimated) && (
                            <div style={{flex:1,background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.05)",borderRadius:10,padding:"9px 11px",textAlign:"center"}}>
                              <div style={{fontSize:12,color:"rgba(255,255,255,0.2)"}}>Estimates TBD</div>
                            </div>
                          )}
                        </div>

                        {/* Pre-Game strip */}
                        <button onClick={()=>getPrep(e)} disabled={bl} className="hma-earn-strip" style={{
                          display:"block",width:"calc(100% + 36px)",marginLeft:-18,marginBottom:-14,marginTop:14,
                          padding:"10px 0",border:"none",borderTop:`1px solid ${brief?"rgba(201,168,76,0.2)":"rgba(255,255,255,0.06)"}`,
                          background:brief
                            ? "linear-gradient(90deg,rgba(201,168,76,0.12),rgba(201,168,76,0.06),rgba(201,168,76,0.12))"
                            : "linear-gradient(90deg,rgba(201,168,76,0.04),rgba(201,168,76,0.08),rgba(201,168,76,0.04))",
                          color:brief?"#ffe87a":"#e8c875",fontWeight:800,fontSize:12,letterSpacing:".08em",
                          textTransform:"uppercase",cursor:bl?"default":"pointer",transition:"all .15s",
                          opacity:bl?0.5:1,borderRadius:"0 0 18px 18px"
                        }}>
                          {bl?<span style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6}}><Clock size={12} strokeWidth={2}/> Generating…</span>:brief?(open?<span style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6}}><ChevronUp size={12} strokeWidth={2}/> Hide Brief</span>:<span style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6}}><Zap size={12} strokeWidth={2}/> Pre-Game</span>):<span style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6}}><Zap size={12} strokeWidth={2}/> Pre-Game</span>}
                        </button>
                      </div>

                      {/* AI Brief panel */}
                      {brief && open && (
                        <div style={{borderTop:`1px solid rgba(201,168,76,0.1)`,background:"rgba(201,168,76,0.025)",padding:"14px 18px 16px"}}>
                          <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10}}>
                            {(brief.watch||[]).map((w,i)=>(
                              <span key={i} style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:10,color:"rgba(255,255,255,0.88)",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.09)",borderRadius:20,padding:"3px 9px"}}><Eye size={9} strokeWidth={2}/> {w}</span>
                            ))}
                          </div>
                          <div className="hma-earn-bull-bear" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                            {brief.bull && <div style={{fontSize:11,color:"#4ade80",lineHeight:1.65,padding:"9px 11px",background:"rgba(74,222,128,0.05)",borderRadius:8,border:"1px solid rgba(74,222,128,0.12)"}}>🐂 {brief.bull}</div>}
                            {brief.bear && <div style={{fontSize:11,color:"#f87171",lineHeight:1.65,padding:"9px 11px",background:"rgba(248,113,113,0.05)",borderRadius:8,border:"1px solid rgba(248,113,113,0.12)"}}>🐻 {brief.bear}</div>}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
